import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ScheduledEvent } from "../../scheduleApi"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from '@dnd-kit/utilities'
import { faGripDotsVertical } from "@fortawesome/pro-duotone-svg-icons"
import { Tooltip } from "antd"
import { EnvironmentOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { getGeocoding } from '../../../RouteManagement/Mappers/mapbox'
import { useUpdateJobPropertyGeolocationMutation } from '../../../RouteManagement/routingQueries'
import { EventAddressModal } from '../EventAddressModal'

export type SortableEventProps = {
  event: ScheduledEvent
  index: number
  isHovered: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  disabled?: boolean
  startTime?: string
  endTime?: string
  onLocationUpdate?: (eventId: number, latitude: number, longitude: number) => void
}

const SortableEvent = ({ event, index, isHovered, onMouseEnter, onMouseLeave, disabled, startTime, endTime, onLocationUpdate }: SortableEventProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: event.id, disabled })

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const { mutate: updateJobPropertyGeolocation, isLoading: isUpdatingJobPropertyGeolocation } = useUpdateJobPropertyGeolocationMutation()

  const hasMissingCoordinates = event.latitude === null || event.longitude === null

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 150ms ease',
    border: isHovered ? '1px solid #FFCABD' : '1px solid transparent',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '12px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: isHovered ? '0 0 0 1px #FFCABD, 0 1px 2px rgba(0,0,0,0.03)' : '0 1px 2px rgba(0,0,0,0.03)',
    outline: undefined,
    outlineOffset: undefined,
    opacity: disabled ? 0.6 : 1,
  }

  const handleLocationClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isUpdatingJobPropertyGeolocation) return

    if (hasMissingCoordinates) {
      const addressString = [
        event.customerAddress1,
        event.customerCity,
        event.customerState,
        event.customerZip
      ].filter(Boolean).join(' ')

      if (addressString.trim()) {
        const result = await getGeocoding(addressString)
        if (result && event.propertyId) {
          const context = result.properties.context || {}
          const address = context.address?.name || result.properties.address_line1 || ''
          const city = context.place?.name || result.properties.context_city || ''
          const state = context.region?.region_code || result.properties.context_region || ''
          const zip = context.postcode?.name || result.properties.context_postcode || ''
          const county = context.district?.name || ''
          const country = context.country?.country_code || 'US'

          const latitude = result.geometry.coordinates[1]
          const longitude = result.geometry.coordinates[0]

          updateJobPropertyGeolocation({
            propertyId: event.propertyId,
            nickname: '',
            address1: address,
            address2: null,
            city: city,
            state: state,
            zip: zip,
            county: county,
            countryCode: country,
            latitude: latitude,
            longitude: longitude
          }, {
            onSuccess: () => {
              if (onLocationUpdate) {
                onLocationUpdate(event.id, latitude, longitude)
              }
            }
          })
        } else {
          setIsAddressModalOpen(true)
        }
      } else {
        setIsAddressModalOpen(true)
      }
    } else {
      setIsAddressModalOpen(true)
    }
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: 8,
              cursor: disabled ? 'not-allowed' : 'grab',
              color: disabled ? '#ccc' : '#888',
              padding: '4px 8px',
              borderRadius: '4px',
            }}
            {...attributes}
            {...listeners}
          >
            <FontAwesomeIcon icon={faGripDotsVertical} style={{ fontSize: '16px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: 24, marginRight: 8 }}>
            <span style={{ fontWeight: 600, color: disabled ? '#ccc' : '#888', fontSize: 13 }}>
              {disabled ? '-' : index + 1}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              flex: 1,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14, color: disabled ? '#999' : 'inherit' }}>{event.jobName}</div>
            <div style={{ fontSize: 13, color: disabled ? '#ccc' : '#888', marginTop: 2 }}>{event.customerDisplayName}</div>
            {event.allDay ? (
              <div style={{ fontSize: 12, color: '#1890ff', marginTop: 4 }}>
                All Day
              </div>
            ) : (startTime || endTime) && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                {startTime && endTime && `${startTime} - ${endTime}`}
              </div>
            )}
          </div>
          <Tooltip title={hasMissingCoordinates ? "Click to resolve missing location" : "Change Address"}>
            <EnvironmentOutlined
              style={{
                color: hasMissingCoordinates ? '#ff4d4f' : '#999',
                fontSize: '16px',
                cursor: isUpdatingJobPropertyGeolocation ? 'wait' : 'pointer',
                marginLeft: 8
              }}
              onClick={handleLocationClick}
            />
          </Tooltip>
        </div>
      </div>

      <EventAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        event={event}
        onAddressSelect={(selectedAddress) => {
          if (onLocationUpdate && selectedAddress) {
            onLocationUpdate(event.id, selectedAddress.latitude, selectedAddress.longitude)
          }
        }}
      />
    </>
  )
}

export default SortableEvent
